import { useState } from "react";
import { Modal, TextInput, Button, Group, Alert } from "@mantine/core";
import { api } from "../services/api";

interface AddFamilyModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddFamilyModal({ opened, onClose, onSuccess }: AddFamilyModalProps) {
  const [familyName, setFamilyName] = useState("");
  const [uniqueLink, setUniqueLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!familyName.trim() || !uniqueLink.trim()) {
      setError("All fields are required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.createFamily(familyName.trim(), uniqueLink.trim());
      setFamilyName("");
      setUniqueLink("");
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create family. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFamilyName("");
    setUniqueLink("");
    setError(null);
    onClose();
  };

  const generateLink = () => {
    const name = familyName.toLowerCase()
      .replace(/\s+family$/i, "") // Remove "Family" suffix
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setUniqueLink(`wedding-2026-${name}`);
  };

  return (
    <Modal 
      opened={opened} 
      onClose={handleClose} 
      title="Add New Family" 
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert color="red" style={{ marginBottom: "1rem" }}>
            {error}
          </Alert>
        )}

        <TextInput
          label="Family Name"
          placeholder="e.g., Smith Family"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          required
          style={{ marginBottom: "1rem" }}
          onBlur={generateLink}
        />

        <TextInput
          label="Unique Link"
          placeholder="e.g., wedding-2026-smith"
          value={uniqueLink}
          onChange={(e) => setUniqueLink(e.target.value)}
          required
          style={{ marginBottom: "1.5rem" }}
          description="This will be used in the URL for the family to access their RSVP"
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Family"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
